import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  CircularProgress,
  Card,
  CardContent,
  CardActionArea,
  Grid,
  Chip,
  Alert,
  Button,
} from "@mui/material";
import DescriptionIcon from "@mui/icons-material/Description";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import DischargeSummaryService from "../../../../services/DischargeSummaryService";

/**
 * TemplateSelectionStep
 *
 * First step of the discharge summary flow.
 * Fetches available templates using only ORG-CODE + APP-MODE headers,
 * lets the user select one, then binds it to the admission via a second API call.
 */
const TemplateSelectionStep = ({
  admissionId,
  authHeaders,
  onTemplateSelected,
}) => {
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [selectedId, setSelectedId] = useState(null);
  const [binding, setBinding] = useState(false);

  // Fetch available templates on mount
  useEffect(() => {
    let cancelled = false;

    const fetchTemplates = async () => {
      setLoading(true);
      setError("");
      try {
        const response = await DischargeSummaryService.listTemplates(authHeaders);

        // Support multiple response shapes from the backend
        const list =
          response?.templates ||
          response?.data?.templates ||
          response?.data ||
          [];

        if (!cancelled) {
          setTemplates(Array.isArray(list) ? list : []);
        }
      } catch (err) {
        console.error("Failed to load discharge templates:", err);
        if (!cancelled) {
          setError(
            err?.response?.data?.message ||
              err?.message ||
              "Failed to load templates."
          );
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    };

    fetchTemplates();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Handle template card click
  const handleSelect = (templateId) => {
    setSelectedId(templateId);
  };

  // Confirm selection → bind template to admission, then advance
  const handleConfirm = async () => {
    if (!selectedId) return;
    setBinding(true);
    setError("");

    try {
      const res = await DischargeSummaryService.selectTemplate(
        admissionId,
        selectedId,
        authHeaders
      );
      // This was the only call in the discharge flow that ignored the
      // status:"Failed" envelope — axios does not reject on it. The `res?.status &&`
      // guard is deliberate: the endpoint does not always send an envelope, and a
      // missing one must not read as a failure.
      if (res?.status && res.status !== "Success") {
        setError(res.message || "Failed to select template. Please try again.");
        return;
      }

      const selectedObj = templates.find((t) => (t.template_id || t.id || t.templateId) === selectedId);
      onTemplateSelected(selectedId, selectedObj);
    } catch (err) {
      console.error("Failed to bind template:", err);
      setError(
        err?.response?.data?.message ||
          err?.message ||
          "Failed to select template. Please try again."
      );
    } finally {
      setBinding(false);
    }
  };

  // ── Loading state ─────────────────────────────────────────────────────
  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          gap: 2,
        }}
      >
        <CircularProgress />
        <Typography variant="body2" color="text.secondary">
          Loading available templates…
        </Typography>
      </Box>
    );
  }

  // ── Error state ───────────────────────────────────────────────────────
  if (error && templates.length === 0) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button
          variant="outlined"
          size="small"
          onClick={() => window.location.reload()}
        >
          Retry
        </Button>
      </Box>
    );
  }

  // ── Empty state ───────────────────────────────────────────────────────
  if (templates.length === 0) {
    return (
      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          minHeight: 300,
          gap: 2,
        }}
      >
        <DescriptionIcon sx={{ fontSize: 48, color: "text.disabled" }} />
        <Typography variant="body1" color="text.secondary">
          No discharge templates available for this organization.
        </Typography>
      </Box>
    );
  }

  // ── Template grid ─────────────────────────────────────────────────────
  return (
    <Box sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ fontWeight: 600, mb: 0.5 }}>
        Select Discharge Template
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Choose a template to generate the discharge summary for this admission.
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <Grid container spacing={2}>
        {templates.map((tmpl) => {
          const templateId =
            tmpl.template_id || tmpl.id || tmpl.templateId;
          const isSelected = selectedId === templateId;
          const name =
            tmpl.template_name || tmpl.name || tmpl.title || "Untitled Template";
          const description =
            tmpl.description || tmpl.template_description || "";

          return (
            <Grid item xs={12} sm={6} md={4} key={templateId}>
              <Card
                variant="outlined"
                sx={{
                  borderColor: isSelected ? "primary.main" : "divider",
                  borderWidth: isSelected ? 2 : 1,
                  backgroundColor: isSelected ? "primary.50" : "#fff",
                  transition: "all 0.2s ease",
                  "&:hover": {
                    borderColor: "primary.light",
                    boxShadow: 2,
                  },
                }}
              >
                <CardActionArea onClick={() => handleSelect(templateId)}>
                  <CardContent
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                      gap: 1,
                      minHeight: tmpl.s3_path ? 280 : 120,
                    }}
                  >
                    {tmpl.s3_path && (
                      <Box sx={{ width: "100%", height: 180, overflow: "hidden", border: "1px solid", borderColor: "divider", borderRadius: 1, mb: 1, position: 'relative' }}>
                         <Box sx={{ position: 'absolute', top: 0, left: 0, width: '100%', height: '100%', zIndex: 10 }} />
                         <iframe 
                           src={`${tmpl.s3_path}#toolbar=0&navpanes=0&scrollbar=0&view=FitH`} 
                           width="100%" 
                           height="100%" 
                           style={{ border: 'none', pointerEvents: 'none', overflow: 'hidden' }}
                           title={`${name} preview`}
                         />
                      </Box>
                    )}
                    <Box
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "space-between",
                      }}
                    >
                      <Box
                        sx={{ display: "flex", alignItems: "center", gap: 1 }}
                      >
                        {!tmpl.s3_path && (
                          <DescriptionIcon
                            color={isSelected ? "primary" : "action"}
                          />
                        )}
                        <Typography
                          variant="subtitle1"
                          sx={{ fontWeight: 600 }}
                        >
                          {name}
                        </Typography>
                      </Box>
                      {isSelected && (
                        <CheckCircleIcon color="primary" fontSize="small" />
                      )}
                    </Box>

                    {description && (
                      <Typography
                        variant="body2"
                        color="text.secondary"
                        sx={{
                          overflow: "hidden",
                          textOverflow: "ellipsis",
                          display: "-webkit-box",
                          WebkitLineClamp: 3,
                          WebkitBoxOrient: "vertical",
                        }}
                      >
                        {description}
                      </Typography>
                    )}

                    {tmpl.version && (
                      <Chip
                        label={`v${tmpl.version}`}
                        size="small"
                        variant="outlined"
                        sx={{ alignSelf: "flex-start" }}
                      />
                    )}
                  </CardContent>
                </CardActionArea>
              </Card>
            </Grid>
          );
        })}
      </Grid>

      {/* Confirm button */}
      <Box
        sx={{
          display: "flex",
          justifyContent: "flex-end",
          mt: 3,
          gap: 1,
        }}
      >
        <Button
          variant="contained"
          color="primary"
          disabled={!selectedId || binding}
          onClick={handleConfirm}
          startIcon={
            binding ? <CircularProgress size={18} color="inherit" /> : null
          }
        >
          {binding ? "Selecting…" : "Use This Template"}
        </Button>
      </Box>
    </Box>
  );
};

export default TemplateSelectionStep;
